import { atom, useAtomValue } from "jotai";
import { searchTermAtom } from "components/Event/Dashboard/utils/filtersAtom";
import { InvitationStatus, OnlineEventItem } from "common/event/event.types";
import { filter, orderBy, partition } from "lodash-es";
import { isContainSubString } from "@ic-anywhere/ic-utils";
import { isDateBeforeToday } from "src/utils/dateUtils";
import { useGetOnlineEventsQuery } from "src/queries/useGetOnlineEventsQuery";

export enum EVENT_STATUS {
  ALL,
  PENDING,
  ATTENDING,
  DECLINED,
  ISSUED,
}

export const activeFilterStatusAtom = atom<EVENT_STATUS>(EVENT_STATUS.ALL);

const hasMatchingStatus = (event: OnlineEventItem, statusFilter: EVENT_STATUS): boolean => {
  switch (statusFilter) {
    case EVENT_STATUS.PENDING:
      return event?.invitation !== undefined && InvitationStatus.Pending === event.invitation.status;
    case EVENT_STATUS.ATTENDING:
      return event?.invitation !== undefined && InvitationStatus.Accepted === event.invitation.status;
    case EVENT_STATUS.DECLINED:
      return (
        event?.invitation !== undefined &&
        [InvitationStatus.DeclinedNotAvailable, InvitationStatus.DeclinedNotInterested].includes(
          event.invitation.status
        )
      );
    case EVENT_STATUS.ISSUED:
      return event.endDate && isDateBeforeToday(event.endDate);
    default:
      return true;
  }
};
const filterAndSortEvents = (
  events: OnlineEventItem[],
  searchTerm: string,
  statusFilter: EVENT_STATUS
): OnlineEventItem[] => {
  const filteredEvents = filter(
    events,
    (event) => isContainSubString(event.name, searchTerm) && hasMatchingStatus(event, statusFilter)
  );
  const [passedEvents, eventsToCome] = partition(filteredEvents, (event) => isDateBeforeToday(event.endDate));
  const orderedEventsToCome = orderBy(eventsToCome, ["startDate"], ["desc"]);
  const orderedPassedEvents = orderBy(passedEvents, ["startDate"], ["desc"]);
  return [...orderedEventsToCome, ...orderedPassedEvents];
};

export interface UseEventsDashboardResult {
  events: OnlineEventItem[];
  isFetching: boolean;
}

export const useEventsDashboard = (): UseEventsDashboardResult => {
  const { data, isFetching } = useGetOnlineEventsQuery();
  const status = useAtomValue(activeFilterStatusAtom);
  const searchTerm = useAtomValue(searchTermAtom);

  if (isFetching || data === undefined) {
    return {
      events: [],
      isFetching,
    };
  }

  return {
    events: filterAndSortEvents(data, searchTerm, status),
    isFetching,
  };
};
